import { Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { NodeInternalWithDetailsListItem } from '../../api/internal_types'
import { NodeInternalWithDetailsTableData } from '../../api/types'
import {
    convertToNodeTableData,
    dateTableSorter,
    getTableFilters,
    localeTableSorter,
    onFilterTableHandler,
} from '../../common/utils'
import './styles.css'

const { Text } = Typography

interface Props {
    data?: NodeInternalWithDetailsListItem[]
    loading?: boolean
}

export const CropTemperatureNodesList: FC<Props> = ({ data, loading }) => {
    const navigate = useNavigate()

    const tableData: NodeInternalWithDetailsTableData[] = useMemo(() => {
        if (!data) {
            return []
        }

        return data
            .filter(
                (
                    item
                ): item is NodeInternalWithDetailsListItem & {
                    node: NonNullable<NodeInternalWithDetailsListItem['node']>
                    lastMeasurement?: NonNullable<NodeInternalWithDetailsListItem['lastMeasurement']>
                } => Boolean(item.node)
            )
            .map((item) => convertToNodeTableData(item))
    }, [data])

    const columns: ColumnsType<NodeInternalWithDetailsTableData> = [
        {
            title: 'Node ID',
            dataIndex: 'nodeId',
            sorter: localeTableSorter<NodeInternalWithDetailsTableData>('nodeId'),
            filterSearch: true,
            filters: getTableFilters<NodeInternalWithDetailsTableData>(tableData, 'nodeId'),
            onFilter: onFilterTableHandler<NodeInternalWithDetailsTableData>('nodeId'),
            fixed: 'left',
            width: 150,
        },
        // Farm and Project
        {
            title: 'Farm',
            dataIndex: 'farmName',
            sorter: localeTableSorter<NodeInternalWithDetailsTableData>('farmName'),
            filterSearch: true,
            filters: getTableFilters<NodeInternalWithDetailsTableData>(tableData, 'farmName'),
            onFilter: onFilterTableHandler<NodeInternalWithDetailsTableData>('farmName'),
            render: (value: string | undefined) => value || '',
            width: 150,
        },
        {
            title: 'Project',
            dataIndex: 'projectName',
            sorter: localeTableSorter<NodeInternalWithDetailsTableData>('projectName'),
            filterSearch: true,
            filters: getTableFilters<NodeInternalWithDetailsTableData>(tableData, 'projectName'),
            onFilter: onFilterTableHandler<NodeInternalWithDetailsTableData>('projectName'),
            render: (value: string | undefined) => value || '',
            width: 150,
        },
        // Cultivation
        {
            title: 'Cultivation',
            dataIndex: 'cultivationName',
            sorter: localeTableSorter<NodeInternalWithDetailsTableData>('cultivationName'),
            filterSearch: true,
            filters: getTableFilters<NodeInternalWithDetailsTableData>(tableData, 'cultivationName'),
            onFilter: onFilterTableHandler<NodeInternalWithDetailsTableData>('cultivationName'),
            render: (value: string | undefined) => value || '',
            width: 150,
        },
        // Node Status
        {
            title: 'Node Status',
            children: [
                {
                    title: 'Battery',
                    dataIndex: 'battery',
                    render: (battery: number) => {
                        let color = battery > 50 ? 'green' : battery > 20 ? 'orange' : 'red'
                        return <span style={{ color }}>{battery}%</span>
                    },
                    width: 100,
                },
                {
                    title: 'Inclination',
                    dataIndex: 'inclination',
                    render: (value: number) => `${value}°`,
                    width: 100,
                },
                {
                    title: 'Firmware',
                    dataIndex: 'firmwareVersion',
                    render: (value: string | undefined) => value || '',
                    filters: getTableFilters<NodeInternalWithDetailsTableData>(tableData, 'firmwareVersion'),
                    onFilter: onFilterTableHandler<NodeInternalWithDetailsTableData>('firmwareVersion'),
                    width: 120,
                },
            ],
        },
        // Location
        {
            title: 'Location',
            children: [
                {
                    title: 'Latitude',
                    dataIndex: 'latitude',
                    render: (value: number | undefined) => value?.toFixed(6) || '',
                    width: 120,
                },
                {
                    title: 'Longitude',
                    dataIndex: 'longitude',
                    render: (value: number | undefined) => value?.toFixed(6) || '',
                    width: 120,
                },
                {
                    title: 'Override',
                    render: (_, record) => {
                        const hasOverride =
                            record.overrideLatitude !== undefined || record.overrideLongitude !== undefined
                        return hasOverride ? (
                            <Tooltip
                                title={
                                    <>
                                        <div>Lat: {record.overrideLatitude?.toFixed(6) ?? 'N/A'}</div>
                                        <div>Lng: {record.overrideLongitude?.toFixed(6) ?? 'N/A'}</div>
                                    </>
                                }
                            >
                                <Tag color="orange">Yes</Tag>
                            </Tooltip>
                        ) : (
                            <Tag color="default">No</Tag>
                        )
                    },
                    width: 100,
                },
            ],
        },
        // Latest Measurement
        {
            title: 'Latest Measurement',
            children: [
                {
                    title: 'Time',
                    dataIndex: 'lastMeasurementTime',
                    render: (date?: Date) =>
                        date ? (
                            <Tooltip title={new Date(date).toLocaleString()}>
                                <Text>{new Date(date).toLocaleDateString()}</Text>
                            </Tooltip>
                        ) : (
                            ''
                        ),
                    sorter: dateTableSorter<NodeInternalWithDetailsTableData>('lastMeasurementTime'),
                    width: 100,
                },
                {
                    title: 'Air Temp',
                    dataIndex: 'lastAirTemperature',
                    render: (temp?: number) =>
                        temp !== undefined ? (
                            <span style={{ color: temp > 30 ? 'red' : temp < 10 ? 'blue' : 'inherit' }}>
                                {temp.toFixed(1)}°C
                            </span>
                        ) : (
                            ''
                        ),
                    width: 100,
                },
                {
                    title: 'Air Humidity',
                    dataIndex: 'lastAirHumidity',
                    render: (humidity?: number) =>
                        humidity !== undefined ? (
                            <span style={{ color: humidity > 80 ? 'blue' : humidity < 30 ? 'orange' : 'inherit' }}>
                                {humidity.toFixed(1)}%
                            </span>
                        ) : (
                            ''
                        ),
                    width: 100,
                },
            ],
        },
        // Calibration
        {
            title: 'Calibration',
            children: [
                {
                    title: 'VWC Model',
                    dataIndex: 'lastCalibrationModelNameVwc',
                    render: (value: string | undefined) => value || '',
                    filters: getTableFilters<NodeInternalWithDetailsTableData>(
                        tableData,
                        'lastCalibrationModelNameVwc'
                    ),
                    onFilter: onFilterTableHandler<NodeInternalWithDetailsTableData>('lastCalibrationModelNameVwc'),
                    width: 150,
                },
                {
                    title: 'EC Model',
                    dataIndex: 'lastCalibrationModelNameEc',
                    render: (value: string | undefined) => value || '',
                    filters: getTableFilters<NodeInternalWithDetailsTableData>(tableData, 'lastCalibrationModelNameEc'),
                    onFilter: onFilterTableHandler<NodeInternalWithDetailsTableData>('lastCalibrationModelNameEc'),
                    width: 150,
                },
            ],
        },
        // Target Configuration
        {
            title: 'Target Configuration',
            children: [
                {
                    title: 'Acquisition (s)',
                    dataIndex: 'targetAcquisitionPeriod',
                    render: (value?: number) => (value !== undefined ? <Text>{value}</Text> : ''),
                    width: 120,
                },
                {
                    title: 'Transmission (s)',
                    dataIndex: 'targetTransmissionPeriod',
                    render: (value?: number) => (value !== undefined ? <Text>{value}</Text> : ''),
                    width: 120,
                },
                {
                    title: 'Updated',
                    dataIndex: 'targetConfigUpdatedAt',
                    render: (date?: Date) =>
                        date ? (
                            <Tooltip title={new Date(date).toLocaleString()}>
                                <Text>{new Date(date).toLocaleDateString()}</Text>
                            </Tooltip>
                        ) : (
                            ''
                        ),
                    sorter: dateTableSorter<NodeInternalWithDetailsTableData>('targetConfigUpdatedAt'),
                    width: 120,
                },
            ],
        },
        // Current Configuration
        {
            title: 'Current Configuration',
            children: [
                {
                    title: 'Acquisition (s)',
                    dataIndex: 'currentAcquisitionPeriod',
                    render: (value?: number) => (value !== undefined ? <Text>{value}</Text> : ''),
                    width: 120,
                },
                {
                    title: 'Transmission (s)',
                    dataIndex: 'currentTransmissionPeriod',
                    render: (value?: number) => (value !== undefined ? <Text>{value}</Text> : ''),
                    width: 120,
                },
                {
                    title: 'Updated',
                    dataIndex: 'currentConfigUpdatedAt',
                    render: (date?: Date) =>
                        date ? (
                            <Tooltip title={new Date(date).toLocaleString()}>
                                <Text>{new Date(date).toLocaleDateString()}</Text>
                            </Tooltip>
                        ) : (
                            ''
                        ),
                    sorter: dateTableSorter<NodeInternalWithDetailsTableData>('currentConfigUpdatedAt'),
                    width: 120,
                },
            ],
        },
    ]

    return (
        <div className="nodes-list__container">
            <Table
                loading={loading}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} nodes`,
                }}
                columns={columns}
                dataSource={tableData}
                rowKey="key"
                scroll={{ x: 1800 }}
                size="middle"
                bordered
                rowClassName="nodes-list__row"
                sticky={{ offsetHeader: 0 }}
            />
        </div>
    )
}
